import React, { useEffect, useState } from 'react';
import { TextField, Button, Container, Checkbox, FormControlLabel } from '@mui/material';
import axios from 'axios';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IMaskInput } from 'react-imask';
import { Header } from '../shared/Header';
import { cpf } from 'cpf-cnpj-validator';
import { TextMaskPhone } from '../shared/phone-mask/phoneMask';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/toastContext';
import useWindowDimensions from '../hooks/windowDimensions';
import { Footer } from '../shared/footer/Footer';

function cleanPhoneNumber(phoneNumber: string) {
  if (!phoneNumber) return phoneNumber;
  const cleanedNumber = phoneNumber.replace(/[\s()\-]/g, '');
  return String(cleanedNumber);
}

function parseApiErrorToToastMsg(error: any) {
  try {
    let errorObj;
    if (error?.response?.data?.detail?.message) {
      errorObj = error?.response?.data?.detail?.message;
    } else if (error?.response?.data?.detail) {
      errorObj = error?.response?.data?.detail;
    } else if (error?.message && typeof error.message === 'string') {
      errorObj = error.message;
    }

    let errorMsg = '';

    if (Array.isArray(errorObj)) {
      errorObj.forEach((error: any) => {
        errorMsg += `${error?.msg}\n`;
      });
    } else if (typeof errorObj === 'string') {
      errorMsg = String(errorObj);
    } else {
      errorMsg = 'Erro ao realizar operação';
    }

    return errorMsg;
  } catch (e) {
    console.error(e);
    return 'Erro ao realizar operação';
  }
}

const siteId = '6Ldynr8qAAAAAKKQadIQUEOJlr8JCjuNnn_peDYi';
const api_url = 'https://api.adm.doctor/affiliate/';

const validationSchema = z.object({
  name: z
    .string()
    .min(3, { message: 'Nome Inválido' })
    .max(50, { message: 'Nome Inválido' })
    .refine((value) => value.trim() !== '', { message: 'Nome é obrigatório' }),
  mobile: z.string().min(3, { message: 'Telefone é inválido' }).max(15, { message: 'Telefone é Inválido' }),
  /*   email: z
    .string()
    .email({ message: 'Formato de e-mail errado' })
    .min(3, { message: 'Email Inválido' })
    .max(50, { message: 'Email Inválido' })
    .refine((value) => value.trim() !== '', {
      message: 'E-Mail é obrigatório',
    }), */
  pix: z.string().min(6, { message: 'Pix é inválido' }).max(100, { message: 'Pix é Inválido' }),
  cpf: z
    .string()
    .refine(
      (value) => {
        if (!value) return true;
        return cpf.isValid(value);
      },
      { message: 'CPF inválido' },
    )
    .refine((value) => value.trim() !== '', { message: 'CPF é obrigatório' }),
});
type ValidationSchema = z.infer<typeof validationSchema>;

const cpfMask = ['000.000.000-00'];

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const TextMaskCpf = React.forwardRef<HTMLElement, CustomProps>(function TextMaskCpf(props, ref: any) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask={cpfMask}
      definitions={{
        '#': /[1-9]/,
      }}
      inputRef={ref}
      onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
      overwrite
    />
  );
});

function Home() {
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { showToastMessage } = useToast();
  const { isMobileOnly, isMobile } = useWindowDimensions();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/enterprise.js?render=' + siteId;
    script.async = true;
    script.onload = () => {
      console.log('reCAPTCHA script loaded');
    };
    document.head.appendChild(script);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<ValidationSchema>({
    mode: 'all',
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (data: ValidationSchema) => {
    const grecaptcha = (window as any).grecaptcha || {};
    console.log('grecaptcha:', grecaptcha);

    if (typeof grecaptcha === 'undefined') {
      console.error('reCAPTCHA not loaded');
      return;
    }

    try {
      grecaptcha.enterprise.ready(async () => {
        const token = await grecaptcha.enterprise.execute(siteId, { action: 'getLink' });
        console.log('reCAPTCHA token:', token);

        const postData = { ...data, 'g-recaptcha-response': token, mobile: cleanPhoneNumber('55' + data.mobile) };
        try {
          const response = await axios.post(api_url, postData);
          localStorage.setItem('url', response.data.url);
          console.log('API response:', response.data);
          navigate('success');
        } catch (axiosError) {
          console.error('Error during axios.post:', axiosError);
          showToastMessage(parseApiErrorToToastMsg(axiosError));
        }
      });
    } catch (error: any) {
      console.error('Error submitting form:', error);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(e.target.checked);
  };

  return (
    <div>
      {isMobile ? (
        <section className="flex flex-col mt-6">
          <div className="mb-6 px-14">
            <img width={200} src="/logos/logo-grey.png"></img>

            <form
              id="demo-form"
              onSubmit={handleSubmit(onSubmit)}
              className="z-10 w-full space-y-4 mt-12"
              style={{ width: '100%' }}
            >
              <div className="">
                <label className="mb-0 block text-sm text-gray-700">Nome*</label>
                <TextField
                  fullWidth
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  margin="dense"
                />
              </div>
              <div className="">
                <label className="mb-0 block text-sm text-gray-700">Telefone*</label>
                <TextField
                  fullWidth
                  {...register('mobile')}
                  error={!!errors.mobile}
                  helperText={errors.mobile?.message}
                  margin="dense"
                  InputProps={{
                    inputComponent: TextMaskPhone as any,
                  }}
                />
              </div>
              <div className="">
                <label className="mb-0 block text-sm text-gray-700">CPF*</label>
                <Controller
                  name="cpf"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.cpf}
                      helperText={errors.cpf?.message}
                      margin="dense"
                      InputProps={{
                        inputComponent: TextMaskCpf as any,
                      }}
                    />
                  )}
                />
              </div>
              <div className="">
                <label className="mb-0 block text-sm text-gray-700">Chave - Pix*</label>
                <TextField
                  fullWidth
                  {...register('pix')}
                  error={!!errors.pix}
                  helperText={errors.pix?.message}
                  margin="dense"
                />
              </div>
              <div className="-mx-2 flex flex-wrap">
                <div className="w-full px-2 text-center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={handleCheckboxChange}
                        name="termsAccepted"
                        color="primary"
                      />
                    }
                    label={
                      <span style={{ fontSize: '13px' }}>
                        Eu concordo com o{' '}
                        <a
                          href="https://adm.doctor/wp-content/uploads/2025/02/Programa-de-Indicacao-Amigo-Medico-Estendido-AdmDoctor-27.jan_.24.docx-1.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline' }}
                        >
                          Regulamento
                        </a>
                      </span>
                    }
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-center">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!termsAccepted || !isValid}
                  color="error"
                  sx={{ width: '130px', textTransform: 'none' }}
                >
                  Enviar
                </Button>
              </div>
            </form>
          </div>
          <Footer isMobile={true}></Footer>
        </section>
      ) : (
        <section className="flex flex-col pt-6">
          <div style={{ minHeight: '69vh', marginLeft: '25vw' }} className="flex items-center gap-5">
            <form
              id="demo-form"
              onSubmit={handleSubmit(onSubmit)}
              className="w-full space-y-1 pr-12"
              style={{ width: '400px' }}
            >
              <Header />
              <div className="pt-6">
                <label className="mb-0 block text-sm text-gray-700">Nome*</label>
                <TextField
                  fullWidth
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  margin="dense"
                />
              </div>
              <div className="">
                <label className="mb-0 block text-sm text-gray-700">Telefone*</label>
                <TextField
                  fullWidth
                  {...register('mobile')}
                  error={!!errors.mobile}
                  helperText={errors.mobile?.message}
                  margin="dense"
                  InputProps={{
                    inputComponent: TextMaskPhone as any,
                  }}
                />
              </div>
              <div className="">
                <label className="mb-0 block text-sm text-gray-700">CPF*</label>
                <Controller
                  name="cpf"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      error={!!errors.cpf}
                      helperText={errors.cpf?.message}
                      margin="dense"
                      InputProps={{
                        inputComponent: TextMaskCpf as any,
                      }}
                    />
                  )}
                />
              </div>
              <div className="relative">
                <label className="mb-0 block text-sm text-gray-700">Chave - Pix*</label>
                <TextField
                  fullWidth
                  {...register('pix')}
                  error={!!errors.pix}
                  helperText={errors.pix?.message}
                  margin="dense"
                />
                <span
                  style={{ color: '#4B4B4B', fontSize: '13px', lineHeight: '0.8' }}
                  className="left-3 top-24 pr-12 font-medium"
                >
                  Pix da conta na qual voce ira receber seus pagamentos. Dever ser uma chave PIX de uma conta vinculado à sua
                  Pessoa Fisica
                </span>
              </div>
              <div className="-mx-2 flex flex-wrap">
                <div className="w-full pr-12 text-center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={handleCheckboxChange}
                        name="termsAccepted"
                        color="primary"
                      />
                    }
                    label={
                      <span>
                        Eu concordo com o{' '}
                        <a
                          href="https://adm.doctor/wp-content/uploads/2025/02/Programa-de-Indicacao-Amigo-Medico-Estendido-AdmDoctor-27.jan_.24.docx-1.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'underline' }}
                        >
                          Regulamento
                        </a>
                      </span>
                    }
                  />
                </div>
              </div>
              <div className="mt-6 mb-4 flex justify-center">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!termsAccepted || !isValid}
                  size="large"
                  color="error"
                  sx={{ background: '#00405E', width: '130px', textTransform: 'none' }}
                >
                  Enviar
                </Button>
              </div>
            </form>
            <div className="absolute right-0">
              <img style={{ height: '75vh' }} src="/public/images/right-image2.svg"></img>
            </div>
          </div>

          <div className="w-full">
            {/*    <img
              className=""
              src="/images/background_divider.png"
              style={{ bottom: '0', marginBottom: '-7.5vh', width: '100vw' }}
            ></img> */}
            <Footer></Footer>
          </div>
        </section>
      )}
    </div>
  );
}

export default Home;
